// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { Text } from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_HOME_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import { collectRailContent, hasRailContent } from "../rail/collectRailContent";
import { type AskV2RailSectionId } from "../rail/types";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ChatPageProps = {
  chatId: string;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ChatPage({ chatId }: AskV2ChatPageProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const transport = useAskV2Transport({ chatId, presentation: "chat" });
  const title = transport.chatTitle?.trim() || t("askV2.pages.chat.title");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const railContent = useMemo(
    () =>
      collectRailContent({
        chat: transport.historyChat,
        messages: transport.historyMessages,
      }),
    [transport.historyChat, transport.historyMessages],
  );
  const showRail = railOpen && hasRailContent(railContent);

  return (
    <AskV2ConversationShell
      testId="ask-v2-chat-page"
      onAddQuotedContext={transport.addQuotedContext}
      header={
        <PageHeader>
          <BreadcrumbHeaderTrail className="min-w-0 flex-1 overflow-hidden">
            <BreadcrumbHeaderRoot
              to={ASK_V2_HOME_PATH}
              label={tCommon("nav.askPuntt")}
              testId="ask-v2-chat-breadcrumb-root"
            />
            <span
              className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
              data-testid="ask-v2-chat-title"
            >
              {title}
            </span>
            <span
              className={`${BREADCRUMB_HEADER_CHIP_CLASS} sr-only`}
              data-testid="ask-v2-chat-id"
            >
              {chatId}
            </span>
          </BreadcrumbHeaderTrail>
          <AskV2RailToggle
            open={railOpen}
            onToggle={() => setRailOpen((current) => !current)}
          />
        </PageHeader>
      }
      composer={<AskV2SummonBar transport={transport} />}
      rail={
        showRail ? (
          <AskV2RightRail
            content={railContent}
            sectionOpen={sectionOpen}
            onSectionOpenChange={(id, open) =>
              setSectionOpen((current) => ({ ...current, [id]: open }))
            }
          />
        ) : null
      }
    >
      {transport.historyLoading && transport.messages.length === 0 ? (
        <AskV2ThreadLoading />
      ) : transport.historyError && transport.messages.length === 0 ? (
        <Text
          as="p"
          size="2"
          color="gray"
          data-testid="ask-v2-thread-error"
          className="py-8 text-center"
        >
          {t("askV2.pages.chat.loadError")}
        </Text>
      ) : (
        <AskV2Thread transport={transport} presentation="chat" />
      )}
    </AskV2ConversationShell>
  );
}
